import { useEffect, useMemo, useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Upload, Trash2, Edit, Download, X, ArrowUpDown, ImageIcon, Smartphone, FileText, ChevronDown, FileType2, Package, FileSpreadsheet, EyeOff, Eye } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { PageLoader } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { formatMoney } from "@/lib/money";
import { toast } from "sonner";
import CatalogExportDialog from "@/components/CatalogExportDialog";

const SORT_OPTIONS = [
  { value: "codigo-asc", label: "Código A → Z" },
  { value: "codigo-desc", label: "Código Z → A" },
  { value: "descripcion-asc", label: "Descripción A → Z" },
  { value: "precio-asc", label: "Precio menor → mayor" },
  { value: "precio-desc", label: "Precio mayor → menor" },
  { value: "costo-asc", label: "Costo menor → mayor" },
  { value: "costo-desc", label: "Costo mayor → menor" },
  { value: "proveedor-asc", label: "Proveedor A → Z" },
  { value: "recientes", label: "Más recientes primero" },
  { value: "antiguos", label: "Más antiguos primero" },
];

const emptyForm = {
  codigo: "",
  codigo_interno: "",
  descripcion: "",
  precio_venta: "",
  costo: "",
  costo_nacionalizado: "",
  proveedor: "",
  variantes: [],
  descuento_pct: "",
  bonif_compra: "",
  bonif_gratis: "",
  categoria: "",
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [csvPreviewData, setCsvPreviewData] = useState([]);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [pdfExportOpen, setPdfExportOpen] = useState(false);
  const [sortBy, setSortBy] = useState("codigo-asc");

  const sortedProducts = useMemo(() => {
    const list = [...products];
    const cmp = (a, b) => (a || "").toString().localeCompare((b || "").toString(), "es", { sensitivity: "base" });
    const num = (a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0);
    switch (sortBy) {
      case "codigo-desc": return list.sort((a, b) => cmp(b.codigo, a.codigo));
      case "descripcion-asc": return list.sort((a, b) => cmp(a.descripcion, b.descripcion));
      case "precio-asc": return list.sort((a, b) => num(a.precio_venta, b.precio_venta));
      case "precio-desc": return list.sort((a, b) => num(b.precio_venta, a.precio_venta));
      case "costo-asc": return list.sort((a, b) => num(a.costo, b.costo));
      case "costo-desc": return list.sort((a, b) => num(b.costo, a.costo));
      case "proveedor-asc": return list.sort((a, b) => cmp(a.proveedor, b.proveedor));
      case "recientes": return list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      case "antiguos": return list.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      case "codigo-asc":
      default: return list.sort((a, b) => cmp(a.codigo, b.codigo));
    }
  }, [products, sortBy]);
  // Unique sorted product categories — memoised so the datalist inside
  // the form dialog doesn't recompute Set+sort on every keystroke.
  const productCategories = useMemo(
    () => Array.from(new Set(products.map(p => p.categoria).filter(Boolean))).sort(),
    [products],
  );
  const [formData, setFormData] = useState(emptyForm);
  const [variantInput, setVariantInput] = useState({ tamano: "", color: "" });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get("/products");
      setProducts(response.data);
    } catch (error) {
      toast.error("Error al cargar productos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        precio_venta: parseFloat(formData.precio_venta) || 0,
        costo: parseFloat(formData.costo) || 0,
        costo_nacionalizado: parseFloat(formData.costo_nacionalizado) || 0,
        descuento_pct: Math.max(0, Math.min(100, parseFloat(formData.descuento_pct) || 0)),
        bonif_compra: Math.max(0, parseInt(formData.bonif_compra) || 0),
        bonif_gratis: Math.max(0, parseInt(formData.bonif_gratis) || 0),
      };
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, { ...data, id: editingProduct.id });
        toast.success("Producto actualizado");
      } else {
        await api.post("/products", data);
        toast.success("Producto creado");
      }
      setIsDialogOpen(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error("Error al guardar producto");
    }
  };

  const handleDelete = (id) => {
    setDeleteTarget({ type: 'single', id });
    setDeleteConfirmOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      toast.error("No hay productos seleccionados");
      return;
    }
    setDeleteTarget({ type: 'bulk' });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteTarget?.type === 'single') {
        await api.delete(`/products/${deleteTarget.id}`);
        toast.success("Producto eliminado");
      } else if (deleteTarget?.type === 'bulk') {
        const response = await api.post('/products/bulk-delete', selectedIds);
        toast.success(response.data.message);
        setSelectedIds([]);
      }
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      fetchProducts();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleActive = async (product) => {
    // Optimistic toggle
    const newState = !(product.activo !== false);
    setProducts(prev => prev.map(p => p.id === product.id ? { ...p, activo: newState } : p));
    try {
      const res = await api.patch(`/products/${product.id}/activo`);
      const final = res.data?.activo;
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, activo: final } : p));
      toast.success(final ? "Producto activado" : "Producto desactivado (oculto del catálogo)");
    } catch {
      // revert
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, activo: !newState } : p));
      toast.error("No se pudo cambiar el estado");
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === products.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(products.map(p => p.id));
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      codigo: product.codigo,
      codigo_interno: product.codigo_interno || "",
      descripcion: product.descripcion,
      precio_venta: product.precio_venta?.toString() || "0",
      costo: product.costo?.toString() || "0",
      costo_nacionalizado: product.costo_nacionalizado?.toString() || "0",
      proveedor: product.proveedor || "",
      variantes: product.variantes || [],
      descuento_pct: product.descuento_pct ? product.descuento_pct.toString() : "",
      bonif_compra: product.bonif_compra ? product.bonif_compra.toString() : "",
      bonif_gratis: product.bonif_gratis ? product.bonif_gratis.toString() : "",
      categoria: product.categoria || "",
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setVariantInput({ tamano: "", color: "" });
  };

  const addVariant = () => {
    if (!variantInput.tamano && !variantInput.color) {
      toast.error("Ingresa al menos tamaño o color");
      return;
    }
    setFormData({
      ...formData,
      variantes: [...formData.variantes, { ...variantInput }]
    });
    setVariantInput({ tamano: "", color: "" });
  };

  const removeVariant = (index) => {
    setFormData({
      ...formData,
      variantes: formData.variantes.filter((_, i) => i !== index)
    });
  };

  const downloadTemplate = async (format = "csv") => {
    try {
      const token = localStorage.getItem("token");
      const url = format === "excel"
        ? `${process.env.REACT_APP_BACKEND_URL}/api/products/template/excel`
        : `${process.env.REACT_APP_BACKEND_URL}/api/products/template`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to download");
      const blob = await response.blob();
      const objUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objUrl;
      link.setAttribute('download', format === "excel" ? 'plantilla_productos.xlsx' : 'plantilla_productos.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objUrl);
      toast.success(`Plantilla ${format === "excel" ? "Excel" : "CSV"} descargada`);
    } catch (error) {
      toast.error("Error al descargar plantilla");
    }
  };

  const downloadWhatsAppCatalog = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp-catalog/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "catalogo_whatsapp.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Catálogo WhatsApp descargado. Subilo a business.facebook.com/commerce/catalogs");
    } catch {
      toast.error("Error al exportar catálogo WhatsApp");
    }
  };

  const downloadCatalogPdf = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/catalog/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("pdf export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "catalogo.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Catálogo PDF generado");
    } catch {
      toast.error("Error al generar catálogo PDF");
    }
  };

  const downloadCatalogTemplate = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/export/template-csv`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("template export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "catalogo_productos.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Catálogo exportado en formato de plantilla");
    } catch {
      toast.error("Error al exportar catálogo en formato plantilla");
    }
  };

  const uploadProductImage = async (product, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagen muy grande (máx 5MB)");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/products/${product.id}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Imagen subida");
      fetchProducts();
    } catch {
      toast.error("Error al subir imagen");
    }
  };

  const deleteProductImage = async (product) => {
    try {
      await api.delete(`/products/${product.id}/image`);
      toast.success("Imagen eliminada");
      fetchProducts();
    } catch {
      toast.error("Error al eliminar imagen");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      let text = await file.text();
      if (text.charCodeAt(0) === 0xFEFF) {
        text = text.slice(1);
      }
      
      const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      };
      
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        toast.error("El CSV está vacío o no tiene datos");
        return;
      }
      
      const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
      
      const previewData = [];
      for (let i = 1; i < Math.min(lines.length, 11); i++) {
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        previewData.push(row);
      }
      
      window._productCsvFile = file;
      setCsvPreviewData(previewData);
      setIsPreviewDialogOpen(true);
    } catch (error) {
      toast.error("Error al leer el archivo CSV");
    }
    
    e.target.value = '';
  };

  const finalizarImportacion = async () => {
    if (!window._productCsvFile) {
      toast.error("No hay archivo para importar");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", window._productCsvFile);

      const response = await api.post("/products/import", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(response.data.message);
      setIsPreviewDialogOpen(false);
      setCsvPreviewData([]);
      window._productCsvFile = null;
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar productos");
    }
  };

  if (loading) {
    return <PageLoader label="Cargando productos…" testid="products-loader" />;
  }

  return (
    <div data-testid="products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Productos
          </h2>
          <p className="text-slate-500 text-sm mt-1">Catálogo de productos</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <Button
              onClick={handleBulkDelete}
              variant="outline"
              className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              data-testid="bulk-delete-products-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar ({selectedIds.length})
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="download-products-template-button">
                <Download className="h-4 w-4 mr-2" />
                Plantilla
                <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuItem onClick={() => downloadTemplate("excel")} className="rounded-sm cursor-pointer" data-testid="download-products-template-excel">
                <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadTemplate("csv")} className="rounded-sm cursor-pointer" data-testid="download-products-template-csv">
                <FileType2 className="h-4 w-4 mr-2 text-slate-600" /> CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="rounded-sm border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="export-catalog-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Exportar catálogo
                <ChevronDown className="h-3.5 w-3.5 ml-1.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm w-64">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">
                Formato
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setPdfExportOpen(true)}
                data-testid="export-catalog-pdf-option"
                className="rounded-sm cursor-pointer py-2"
              >
                <FileText className="h-4 w-4 mr-2 text-rose-600" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">PDF estilo catálogo</span>
                  <span className="text-[11px] text-slate-500">Elegí categorías y orden antes de exportar</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={downloadWhatsAppCatalog}
                data-testid="export-catalog-csv-option"
                className="rounded-sm cursor-pointer py-2"
              >
                <Smartphone className="h-4 w-4 mr-2 text-emerald-700" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">CSV WhatsApp</span>
                  <span className="text-[11px] text-slate-500">Meta Commerce Catalog</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={downloadCatalogTemplate}
                data-testid="export-catalog-template-option"
                className="rounded-sm cursor-pointer py-2"
              >
                <FileType2 className="h-4 w-4 mr-2 text-[rgb(0,47,167)]" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">CSV en formato plantilla</span>
                  <span className="text-[11px] text-slate-500">Editá y reimportá tu catálogo</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="import-products-input"
            />
            <Button
              type="button"
              onClick={(e) => e.currentTarget.previousElementSibling.click()}
              variant="outline"
              className="rounded-sm"
              data-testid="import-products-button"
            >
              <Upload className="h-4 w-4 mr-2" />
              Importar
            </Button>
          </label>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingProduct(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary rounded-sm" data-testid="add-product-button">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Producto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? "Editar Producto" : "Nuevo Producto"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Código *</label>
                    <Input
                      data-testid="product-code-input"
                      value={formData.codigo}
                      onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                      required
                      className="rounded-sm mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Código Interno</label>
                    <Input
                      data-testid="product-internal-code-input"
                      value={formData.codigo_interno}
                      onChange={(e) => setFormData({ ...formData, codigo_interno: e.target.value })}
                      className="rounded-sm mono"
                      placeholder="Código para órdenes de compra"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1.5">Descripción *</label>
                    <Input
                      data-testid="product-description-input"
                      value={formData.descripcion}
                      onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                      required
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Proveedor</label>
                    <Input
                      data-testid="product-supplier-input"
                      value={formData.proveedor}
                      onChange={(e) => setFormData({ ...formData, proveedor: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Categoría</label>
                    <Input
                      data-testid="product-category-input"
                      value={formData.categoria}
                      onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                      className="rounded-sm"
                      placeholder="Ej: Bebidas, Snacks, Limpieza…"
                      list="product-categories-list"
                    />
                    <datalist id="product-categories-list">
                      {productCategories.map(c => (
                        <option key={c} value={c} />
                      ))}
                    </datalist>
                    <p className="text-[11px] text-slate-500 mt-1">Se usa para agrupar el catálogo PDF y filtros internos.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Precio Venta (USD) *</label>
                    <Input
                      data-testid="product-price-input"
                      type="number"
                      step="0.01"
                      value={formData.precio_venta}
                      onChange={(e) => setFormData({ ...formData, precio_venta: e.target.value })}
                      required
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Costo (USD)</label>
                    <Input
                      data-testid="product-cost-input"
                      type="number"
                      step="0.01"
                      value={formData.costo}
                      onChange={(e) => setFormData({ ...formData, costo: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Costo Nacionalizado (USD)</label>
                    <Input
                      data-testid="product-nationalized-cost-input"
                      type="number"
                      step="0.01"
                      value={formData.costo_nacionalizado}
                      onChange={(e) => setFormData({ ...formData, costo_nacionalizado: e.target.value })}
                      className="rounded-sm"
                      placeholder="Costo + impuestos + nacionalización"
                    />
                  </div>
                </div>

                {/* Promociones — descuento porcentual + bonificación "lleva X+Y" */}
                <div className="border border-slate-200 rounded-sm p-4 bg-amber-50/30">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-sm bg-amber-500 text-white text-[10px] font-bold">%</span>
                    <h4 className="font-medium">Promociones</h4>
                    <span className="text-[10px] text-slate-500">opcional — aparecen en catálogos y se aplican al tomar pedido</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1 text-slate-700">Descuento %</label>
                      <Input
                        data-testid="product-discount-input"
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={formData.descuento_pct}
                        onChange={(e) => setFormData({ ...formData, descuento_pct: e.target.value })}
                        className="rounded-sm"
                        placeholder="Ej: 10"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1 text-slate-700">Bonificación · compra</label>
                      <Input
                        data-testid="product-bonif-compra-input"
                        type="number"
                        min="0"
                        step="1"
                        value={formData.bonif_compra}
                        onChange={(e) => setFormData({ ...formData, bonif_compra: e.target.value })}
                        className="rounded-sm"
                        placeholder="Ej: 12"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1 text-slate-700">Bonificación · gratis</label>
                      <Input
                        data-testid="product-bonif-gratis-input"
                        type="number"
                        min="0"
                        step="1"
                        value={formData.bonif_gratis}
                        onChange={(e) => setFormData({ ...formData, bonif_gratis: e.target.value })}
                        className="rounded-sm"
                        placeholder="Ej: 1"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">
                    Ej: 12 + 1 = al cargar 12 unidades, se agrega una bonificación de 1 unidad gratis al pedido.
                  </p>
                </div>

                {/* Imagen del producto */}
                <div className="border border-slate-200 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ImageIcon className="h-4 w-4 text-[rgb(0,47,167)]" />
                    <h4 className="font-medium">Imagen del Producto</h4>
                    <span className="text-[10px] text-slate-500">para catálogo WhatsApp</span>
                  </div>
                  {!editingProduct ? (
                    <div className="text-xs text-slate-500 italic p-3 bg-slate-50 rounded-sm border border-slate-200">
                      Guarda primero el producto para poder subir una imagen.
                    </div>
                  ) : editingProduct.image_url ? (
                    <div className="flex items-start gap-3">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/products/${editingProduct.id}/image?t=${Date.now()}`}
                        alt="Producto"
                        className="w-32 h-32 object-cover rounded-sm border border-slate-200"
                        data-testid="product-image-preview"
                      />
                      <div className="flex flex-col gap-2">
                        <label>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) uploadProductImage(editingProduct, f);
                              e.target.value = "";
                            }}
                            data-testid="product-image-input"
                          />
                          <Button type="button" variant="outline" className="rounded-sm cursor-pointer"
                            onClick={(e) => e.currentTarget.previousSibling.click()}>
                            <Upload className="h-3.5 w-3.5 mr-2" />
                            Reemplazar
                          </Button>
                        </label>
                        <Button type="button" variant="outline"
                          className="rounded-sm text-red-700 border-red-200 hover:bg-red-50"
                          onClick={() => deleteProductImage(editingProduct)}>
                          <X className="h-3.5 w-3.5 mr-2" />
                          Eliminar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <label>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadProductImage(editingProduct, f);
                          e.target.value = "";
                        }}
                        data-testid="product-image-input"
                      />
                      <Button type="button" variant="outline" className="rounded-sm w-full cursor-pointer"
                        onClick={(e) => e.currentTarget.previousSibling.click()}>
                        <Upload className="h-4 w-4 mr-2" />
                        Subir imagen (JPG, PNG, WebP — máx 5MB)
                      </Button>
                    </label>
                  )}
                </div>

                <div className="border border-slate-200 rounded-sm p-4">
                  <h4 className="font-medium mb-3">Variantes (Tamaños y Colores)</h4>
                  <div className="grid grid-cols-12 gap-2 mb-3">
                    <div className="col-span-5">
                      <Input
                        data-testid="variant-size-input"
                        placeholder="Tamaño (ej: S, M, L)"
                        value={variantInput.tamano}
                        onChange={(e) => setVariantInput({ ...variantInput, tamano: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                    <div className="col-span-5">
                      <Input
                        data-testid="variant-color-input"
                        placeholder="Color (ej: Rojo)"
                        value={variantInput.color}
                        onChange={(e) => setVariantInput({ ...variantInput, color: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <Button
                        type="button"
                        onClick={addVariant}
                        className="w-full btn-primary rounded-sm"
                        data-testid="add-variant-button"
                      >
                        Agregar
                      </Button>
                    </div>
                  </div>

                  {formData.variantes.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
                      {formData.variantes.map((variant, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-sm text-sm"
                        >
                          <span>
                            {variant.tamano && <span className="font-medium">{variant.tamano}</span>}
                            {variant.tamano && variant.color && <span className="mx-1 text-slate-400">·</span>}
                            {variant.color && <span>{variant.color}</span>}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeVariant(index)}
                            className="text-slate-500 hover:text-red-600"
                            data-testid={`remove-variant-${index}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="rounded-sm"
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" className="btn-primary rounded-sm" data-testid="save-product-button">
                    {editingProduct ? "Actualizar" : "Crear"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Vista Previa de Importación CSV</DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                {csvPreviewData.length > 0 ? (
                  <>
                    <p className="text-sm text-slate-600 mb-4">
                      Se importarán {csvPreviewData.length} productos. Revisa los datos antes de finalizar.
                    </p>
                    <div className="border border-slate-200 rounded-sm overflow-x-auto max-h-96">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Código</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Descripción</th>
                            <th className="text-right py-2 px-3 font-medium text-slate-600">Precio Venta</th>
                            <th className="text-right py-2 px-3 font-medium text-slate-600">Costo</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Proveedor</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Tamaños</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Colores</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvPreviewData.map((row, index) => (
                            <tr key={index} className="border-b border-slate-100">
                              <td className="py-2 px-3 mono">{row.codigo || row.código || '-'}</td>
                              <td className="py-2 px-3">{row.descripcion || row.descripción || '-'}</td>
                              <td className="py-2 px-3 text-right">${row.precio_venta || row.precio || '0'}</td>
                              <td className="py-2 px-3 text-right">${row.costo || '0'}</td>
                              <td className="py-2 px-3">{row.proveedor || '-'}</td>
                              <td className="py-2 px-3">{row.tamanos || row.tamaños || '-'}</td>
                              <td className="py-2 px-3">{row.colores || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsPreviewDialogOpen(false);
                          setCsvPreviewData([]);
                          window._productCsvFile = null;
                        }}
                        className="rounded-sm"
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={finalizarImportacion}
                        className="btn-primary rounded-sm"
                        data-testid="finalize-products-import-button"
                      >
                        Finalizar Importación
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-center py-8 text-slate-500">No hay datos para previsualizar</p>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-white border border-slate-200 rounded-sm p-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <ArrowUpDown className="h-4 w-4 text-slate-500" />
          <span className="text-xs font-medium text-slate-700 uppercase tracking-wider">Ordenar por:</span>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="rounded-sm h-8 text-xs w-64" data-testid="products-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-700 ml-auto">{sortedProducts.length} producto{sortedProducts.length === 1 ? "" : "s"}</span>
        </div>
      </Card>

      <Card className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {sortedProducts.length === 0 ? (
          <EmptyState
            icon={Package}
            title={products.length === 0 ? "Aún no hay productos" : "Sin coincidencias"}
            description={
              products.length === 0
                ? "Carga tu primer producto, importa un CSV o suma variantes con imágenes para activar tu catálogo."
                : "No encontramos productos con esos filtros. Probá ajustando la búsqueda."
            }
            actionLabel={products.length === 0 ? "Nuevo producto" : undefined}
            actionOnClick={products.length === 0 ? () => { resetForm(); setIsDialogOpen(true); } : undefined}
            testid="products-empty-state"
          />
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="py-3 px-4 w-12">
                  <Checkbox
                    checked={sortedProducts.length > 0 && selectedIds.length === sortedProducts.length}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-products-checkbox"
                  />
                </th>
                <th className="py-3 px-4 w-14">Imagen</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700 mono">Código</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700 mono">Cód. Interno</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Descripción</th>
                <th className="text-right py-3 px-4 font-medium text-slate-700">Precio Venta</th>
                <th className="text-right py-3 px-4 font-medium text-slate-700">Costo</th>
                <th className="text-right py-3 px-4 font-medium text-slate-700">Costo Nac.</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Proveedor</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Variantes</th>
                <th className="text-center py-3 px-4 font-medium text-slate-700">Activo</th>
                <th className="text-right py-3 px-4 font-medium text-slate-700">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sortedProducts.map((product) => {
                const isActive = product.activo !== false;
                return (
                  <tr
                    key={product.id}
                    className={`table-row border-b border-slate-100 cursor-pointer ${!isActive ? "bg-slate-50/60 opacity-60" : ""}`}
                    data-testid="product-row"
                    onClick={() => handleEdit(product)}
                  >
                    <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(product.id)}
                        onCheckedChange={() => toggleSelect(product.id)}
                        data-testid={`select-product-${product.id}`}
                      />
                    </td>
                    <td className="py-2 px-2">
                      {product.image_url ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/products/${product.id}/image`}
                          alt={product.descripcion}
                          className="w-10 h-10 object-cover rounded-sm border border-slate-200"
                          data-testid={`product-thumb-${product.id}`}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-sm border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center">
                          <ImageIcon className="h-4 w-4 text-slate-300" />
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 font-medium mono">
                      <div className="flex items-center gap-1.5">
                        {product.codigo}
                        {!isActive && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200" title="Producto desactivado">
                            Agotado
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 mono text-slate-600">{product.codigo_interno || '-'}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-0.5">
                        <span>{product.descripcion}</span>
                        {(product.descuento_pct > 0 || (product.bonif_compra > 0 && product.bonif_gratis > 0)) && (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {product.descuento_pct > 0 && (
                              <span className="inline-flex items-center text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-rose-50 text-rose-700 border border-rose-200" data-testid={`product-discount-badge-${product.id}`}>
                                {product.descuento_pct}% OFF
                              </span>
                            )}
                            {product.bonif_compra > 0 && product.bonif_gratis > 0 && (
                              <span className="inline-flex items-center text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`product-bonus-badge-${product.id}`}>
                                {product.bonif_compra}+{product.bonif_gratis} GRATIS
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right font-medium">{formatMoney(product.precio_venta || 0)}</td>
                    <td className="py-3 px-4 text-right text-slate-600">{formatMoney(product.costo || 0)}</td>
                    <td className="py-3 px-4 text-right text-slate-600">{formatMoney(product.costo_nacionalizado || 0)}</td>
                    <td className="py-3 px-4 text-slate-600">{product.proveedor || '-'}</td>
                    <td className="py-3 px-4">
                      {product.variantes && product.variantes.length > 0 ? (
                        <span className="text-xs px-2 py-0.5 bg-slate-100 rounded-sm font-medium">
                          {product.variantes.length} variantes
                        </span>
                      ) : '-'}
                    </td>
                    <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1.5">
                        <Switch
                          checked={isActive}
                          onCheckedChange={() => toggleActive(product)}
                          data-testid={`toggle-active-${product.id}`}
                          title={isActive ? "Activo en catálogo" : "Oculto del catálogo"}
                        />
                        {isActive ? (
                          <Eye className="h-3.5 w-3.5 text-emerald-600" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-slate-400" />
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        <Button
                          data-testid={`edit-product-${product.id}`}
                          onClick={() => handleEdit(product)}
                          variant="ghost"
                          size="sm"
                          className="rounded-sm"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`delete-product-${product.id}`}
                          onClick={() => handleDelete(product.id)}
                          variant="ghost"
                          size="sm"
                          className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </Card>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'bulk'
                ? `Estás a punto de eliminar ${selectedIds.length} producto(s). Esta acción no se puede deshacer.`
                : 'Estás a punto de eliminar este producto. Esta acción no se puede deshacer.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-product-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-product-button"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CatalogExportDialog open={pdfExportOpen} onOpenChange={setPdfExportOpen} />
    </div>
  );
}
